<template>
  <full-screen :title="t('hardware-selector.title')" @close="router.back()" show-close>
    <div class="flex flex-col w-full sm:w-1/2 px-4 sm:px-0 m-auto py-4">
      <l-option-selector
        v-if="options.length > 1"
        v-model="selectedOption"
        :options="options"
        class="mb-4"
      />
      <div v-else class="text-3xl text-n-0 font-bold text-center mb-4">
        {{ t('hardware-selector.till') }}
      </div>

      <card-option-selector
        v-model="selectedSubOption[selectedOption]"
        :options="subOptions[selectedOption]"
      />
    </div>
  </full-screen>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { LOptionSelector } from '@last/core-ui/paprika'

import FullScreen from '@/components/FullScreen.vue'
import { useConfigStore } from '@/store/config'
import { useTillStore } from '@/store/till'

import CardOptionSelector from './CardOptionSelector.vue'

const configStore = useConfigStore()
const tillStore = useTillStore()
const router = useRouter()

const { t } = useI18n()

const { updateHardwarePreferences } = configStore
const { tills, device, config } = storeToRefs(configStore)
const { setTill } = tillStore
const { selectedCashTillIsStarted, selectedCashTill, shiftsEnabled } = storeToRefs(tillStore)

type HardwareOptions = 'till' | 'cashMachine' | 'dataphone'
type SelectedSubOption = Record<HardwareOptions, string>

const selectedOption = ref<HardwareOptions>('till')
const selectedSubOption = ref<SelectedSubOption>({
  till: selectedCashTill.value?.id || '',
  cashMachine: device.value.preferredCashMachine || '',
  dataphone: device.value.preferredDataphone || ''
})

watch(
  selectedSubOption,
  newValue => {
    updatePreferences(selectedOption.value, newValue[selectedOption.value])
  },
  {
    deep: true
  }
)

const options = computed(() => {
  const opts = [{ label: t('hardware-selector.till'), value: 'till' }]

  if (subOptions.value.cashMachine.length > 0) {
    opts.push({
      label: t('hardware-selector.cash-machine'),
      value: 'cashMachine'
    })
  }

  if (subOptions.value.dataphone.length > 0) {
    opts.push({ label: t('hardware-selector.dataphone'), value: 'dataphone' })
  }

  return opts
})

const subOptions = computed((): Record<HardwareOptions, any> => {
  const tillsOptions = tills.value.cash.map(cashTill => ({
    value: cashTill.id,
    label: cashTill.name,
    type: 'till'
  }))

  const cashMachines = config.value.paymentMethodsConfig.filter(paymentMethod => {
    return paymentMethod.configs
  })

  const cashMachinesOptions = cashMachines.reduce(
    (acc, cashMachine) => {
      for (const config of cashMachine.configs ?? []) {
        acc.push({
          value: config.id,
          label: `${cashMachine.name} - ${config.name}`,
          type: 'cashMachine'
        })
      }
      return acc
    },
    [] as { value: string; label: string; type: HardwareOptions }[]
  )

  const dataphonesOptions = computed(() => {
    const redsysDataphone = config.value.dataphoneConfigs.map(dataphone => ({
      value: dataphone.id,
      label: dataphone.name,
      type: 'dataphone'
    }))

    const adyenDataphone = config.value.adyenDataphones.map(dataphone => ({
      value: dataphone.id,
      label: dataphone.poiid,
      type: 'dataphone'
    }))

    const integratorTerminals = config.value.terminals.map(terminal => ({
      value: terminal.terminalId,
      label: terminal.name,
      type: 'dataphone'
    }))

    return [...redsysDataphone, ...adyenDataphone, ...integratorTerminals]
  })

  return {
    till: tillsOptions,
    cashMachine: cashMachinesOptions,
    dataphone: dataphonesOptions.value
  }
})

function updatePreferences(type: HardwareOptions, newValue: string) {
  if (type === 'till') {
    const selectedTill = tills.value.cash.find(till => till.id === newValue)
    setTill(selectedTill!)
    if (shiftsEnabled.value && !selectedCashTillIsStarted.value) {
      router.push({ name: 'startShift' })
    }
    updateHardwarePreferences({
      preferredTill: newValue
    })
  } else if (type === 'cashMachine') {
    updateHardwarePreferences({
      preferredCashMachine: newValue
    })
  } else if (type === 'dataphone') {
    updateHardwarePreferences({
      preferredDataphone: newValue
    })
  }
}
</script>
